import React, { useState, useEffect, useContext, useRef } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBoxOpen, faSave } from '@fortawesome/free-solid-svg-icons'
import Swal from 'sweetalert2'
import { useParams } from 'react-router-dom'
import CustomLoader from '../../layouts/CustomLoader'
import AuthContext from '../../../middlewares/AuthContext'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface ProductData {
  id            : number
  name          : string
  description   : string
  list_price    : number
  list_price_2  : number
  list_price_mx : number
  list_price_mx_2: number
  visibility    : boolean
  order         : number
  image         : string
  stripe_id     : string
}

interface EditForm {
  name           : string
  description    : string
  list_price     : number
  list_price_2   : number
  list_price_mx  : number
  list_price_mx_2: number
  visibility     : number
  order          : number
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </label>
)

const EditProduct: React.FC = () => {
  const { getHeaders } = useContext(AuthContext) as AuthCtx
  const { t }          = useTranslation()
  const { id }         = useParams<{ id: string }>()

  const fileInputRef = useRef<HTMLInputElement>(null)

  const [product, setProduct]             = useState<ProductData | null>(null)
  const [initialValues, setInitialValues] = useState<EditForm | null>(null)
  const [loaded, setLoaded]               = useState(false)
  const [loading, setLoading]             = useState(false)
  const [image, setImage]                 = useState<File | null>(null)
  const [preImage, setPreImage]           = useState<string | null>(null)

  const formSchema = Yup.object().shape({
    name           : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    description    : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    list_price     : Yup.number().min(1).required(t('utilities.is_a_required_field')),
    list_price_2   : Yup.number().min(1).required(t('utilities.is_a_required_field')),
    list_price_mx  : Yup.number().min(1).required(t('utilities.is_a_required_field')),
    list_price_mx_2: Yup.number().min(1).required(t('utilities.is_a_required_field')),
    order          : Yup.number().min(1).max(99).required(t('utilities.is_a_required_field')),
    visibility     : Yup.number().min(1).max(2).required(t('utilities.is_a_required_field')),
  })

  useEffect(() => {
    const fetch = async () => {
      try {
        const headers  = await getHeaders()
        const res      = await axios.get(`${url}products/edit/${id}`, { headers })
        const p: ProductData = res.data.message
        setProduct(p)
        setPreImage(p.image)
        setInitialValues({
          name           : p.name,
          description    : p.description,
          list_price     : p.list_price,
          list_price_2   : p.list_price_2,
          list_price_mx  : p.list_price_mx,
          list_price_mx_2: p.list_price_mx_2,
          visibility     : p.visibility ? 2 : 1,
          order          : p.order,
        })
      } catch (err: any) {
        Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
      } finally {
        setLoaded(true)
      }
    }
    fetch()
  }, [])

  const handleImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const ext     = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase()
    const allowed = ['png', 'jpg', 'jpeg', 'gif', 'bmp']
    if (!allowed.includes(ext)) {
      setImage(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      Swal.fire({
        icon: 'info', title: t('products.invalid_extension'),
        toast: true, position: 'top-end',
        showConfirmButton: false, timer: 3000, timerProgressBar: true,
      })
      return
    }
    setImage(file)
  }

  const handleSubmit = async (values: EditForm) => {
    setLoading(true)
    try {
      const formData = new FormData()
      Object.entries(values).forEach(([k, v]) => formData.append(k, String(v)))
      if (image) formData.append('image', image)
      const headers = { ...(await getHeaders()), 'Content-Type': 'multipart/form-data' }
      const res     = await axios.put(`${url}products/update/${id}`, formData, { headers })
      Swal.fire({ icon: 'success', title: res.data.response })
      setImage(null)
      setPreImage(res.data.message.image)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  if (!loaded) return <CustomLoader />

  if (!product || !initialValues) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8', fontWeight: 700 }}>
        {t('products.plans.not_exist')}
      </div>
    )
  }

  return (
    <div className="row justify-content-center">
      <div className="col-md-10 col-lg-8">

        {/* Header */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', overflow: 'hidden', marginBottom: '1.5rem' }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: `${BRAND}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FontAwesomeIcon icon={faBoxOpen} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <div>
              {product.stripe_id && (
                <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Stripe: {product.stripe_id}
                </p>
              )}
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                {t('products.create.edition')}
              </p>
            </div>
          </div>
        </div>

        {/* Form card */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
          <div style={{ height: 3, backgroundColor: BRAND }} />
          <div style={{ padding: '1.5rem' }}>

            <Formik initialValues={initialValues} validationSchema={formSchema} onSubmit={handleSubmit} enableReinitialize>
              <Form>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <FieldLabel>{t('products.create.name')} *</FieldLabel>
                    <Field type="text" className="form-control form-control-sm" name="name" />
                    <ErrorMessage name="name" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                  </div>
                  <div className="col-md-6">
                    <FieldLabel>{t('products.create.description')} *</FieldLabel>
                    <Field type="text" className="form-control form-control-sm" name="description" />
                    <ErrorMessage name="description" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                  </div>
                </div>

                {/* USD prices */}
                <div style={{ backgroundColor: '#f8fafc', borderRadius: 10, padding: '1rem', marginBottom: '1rem' }}>
                  <p style={{ margin: '0 0 0.75rem', fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Precios USD
                  </p>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <FieldLabel>{t('products.create.list_price')} *</FieldLabel>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">$</span>
                        <Field type="number" className="form-control" name="list_price" />
                        <span className="input-group-text">USD</span>
                      </div>
                      <ErrorMessage name="list_price" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="col-md-6">
                      <FieldLabel>{t('products.create.list_price_2')} *</FieldLabel>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">$</span>
                        <Field type="number" className="form-control" name="list_price_2" />
                        <span className="input-group-text">USD</span>
                      </div>
                      <ErrorMessage name="list_price_2" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                  </div>
                </div>

                {/* MXN prices */}
                <div style={{ backgroundColor: '#f8fafc', borderRadius: 10, padding: '1rem', marginBottom: '1rem' }}>
                  <p style={{ margin: '0 0 0.75rem', fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Precios MXN
                  </p>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <FieldLabel>Precio de lista MXN *</FieldLabel>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">$</span>
                        <Field type="number" className="form-control" name="list_price_mx" />
                        <span className="input-group-text">MXN</span>
                      </div>
                      <ErrorMessage name="list_price_mx" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="col-md-6">
                      <FieldLabel>Precio de lista 2 años MXN *</FieldLabel>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">$</span>
                        <Field type="number" className="form-control" name="list_price_mx_2" />
                        <span className="input-group-text">MXN</span>
                      </div>
                      <ErrorMessage name="list_price_mx_2" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <FieldLabel>{t('products.create.order')} *</FieldLabel>
                    <Field type="number" className="form-control form-control-sm" name="order" />
                    <ErrorMessage name="order" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                  </div>
                  <div className="col-md-4">
                    <FieldLabel>{t('products.create.select_an_option')} *</FieldLabel>
                    <Field as="select" className="form-select form-select-sm" name="visibility">
                      <option value={1}>{t('products.hidden')}</option>
                      <option value={2}>{t('products.show')}</option>
                    </Field>
                    <ErrorMessage name="visibility" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                  </div>
                  <div className="col-md-4">
                    <FieldLabel>{t('products.create.select_image')}</FieldLabel>
                    <input
                      ref={fileInputRef}
                      className="form-control form-control-sm"
                      type="file"
                      onChange={handleImage}
                      accept=".png,.jpg,.jpeg,.gif,.bmp"
                    />
                  </div>
                </div>

                {/* Image preview */}
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <p style={{ margin: '0 0 0.4rem', fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                      Current
                    </p>
                    <img
                      src={`${url}images/${preImage}`}
                      alt="current"
                      className="img-fluid rounded"
                      style={{ maxHeight: 140, objectFit: 'contain', opacity: image ? 0.3 : 1, transition: 'opacity 0.2s' }}
                    />
                  </div>
                  {image && (
                    <div className="col-md-6">
                      <p style={{ margin: '0 0 0.4rem', fontSize: '0.72rem', fontWeight: 700, color: BRAND, textTransform: 'uppercase' }}>
                        New
                      </p>
                      <img
                        src={URL.createObjectURL(image)}
                        alt="new"
                        className="img-fluid rounded"
                        style={{ maxHeight: 140, objectFit: 'contain' }}
                      />
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%', padding: '0.55rem 1.5rem', borderRadius: 9999, backgroundColor: loading ? '#94a3b8' : BRAND, color: '#fff', fontWeight: 700, fontSize: '0.875rem', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', boxShadow: loading ? 'none' : `0 4px 14px ${BRAND}55`, transition: 'filter 0.15s, transform 0.15s' }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' } }}
                  onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                >
                  {loading
                    ? <span className="spinner-border spinner-border-sm" role="status" />
                    : <FontAwesomeIcon icon={faSave} />
                  }
                  <span>{t('products.create.update')}</span>
                </button>

              </Form>
            </Formik>

          </div>
        </div>

      </div>
    </div>
  )
}

export default EditProduct
